/**
 * Live Stripe Connect status for an organization (ADR 0040). Fetches lazily
 * from GET /organizations/:id/stripeStatus and refetches when the tab regains
 * focus/visibility — Stripe onboarding opens in a NEW tab, so refocus is the
 * moment the status may have just changed.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth/clerkShim';
import useOrganizationStore from '@store/organizationStore';
import { trackEvent } from '@/utils/analytics';

// Conversion: fire `payments_connected` exactly once, and only for an org this
// browser OBSERVED transitioning to chargesEnabled (a "pending" marker is set
// the first time we see the org NOT enabled). Firing on every enabled read
// would count long-connected orgs as fresh conversions on each new device.
function trackPaymentsConnectedTransition(orgId: string, status: StripeConnectStatus): void {
  if (status.error) return;
  const pendingKey = `staty-ga-pay-pending-${orgId}`;
  try {
    if (!status.chargesEnabled) {
      localStorage.setItem(pendingKey, '1');
    } else if (localStorage.getItem(pendingKey)) {
      localStorage.removeItem(pendingKey);
      trackEvent('payments_connected');
    }
  } catch {
    // Storage unavailable (private mode) — skip rather than crash the poll.
  }
}

export interface StripeConnectStatus {
  hasAccount: boolean;
  detailsSubmitted?: boolean;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  requirementsDue?: string[];
  disabledReason?: string | null;
  // Payout-account summary for the "Payout account" panel (ADR 0051):
  // "Bank ending •••• 1234". Null until a bank account is attached.
  externalAccount?: { last4: string | null; bankName: string | null; currency: string | null } | null;
  error?: 'unavailable';
}

const REFETCH_THROTTLE_MS = 3000;

export default function useStripeConnectStatus(orgId: string | undefined | null): {
  status: StripeConnectStatus | null;
  loading: boolean;
  refetch: () => void;
} {
  const { getToken } = useAuth();
  const getStripeStatus = useOrganizationStore((s: any) => s.getStripeStatus(getToken));
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(!!orgId);
  const lastFetchAt = useRef(0);
  const getStripeStatusRef = useRef(getStripeStatus);
  getStripeStatusRef.current = getStripeStatus;

  const refetch = useCallback(() => {
    if (!orgId) return;
    lastFetchAt.current = Date.now();
    getStripeStatusRef.current(orgId).then((result: StripeConnectStatus | null) => {
      if (result) {
        setStatus(result);
        trackPaymentsConnectedTransition(orgId, result);
      }
      setLoading(false);
    });
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return undefined;
    setLoading(true);
    refetch();

    const onWake = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetchAt.current < REFETCH_THROTTLE_MS) return;
      refetch();
    };
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);
    return () => {
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, [orgId, refetch]);

  return { status, loading, refetch };
}
